import { Injectable } from '@angular/core'
import { SafeUrl } from '@angular/platform-browser'

import { DataSource } from '@angular/cdk/table'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { LEMONATOR_HTTPS_IMAGES_API } from '../../_angular/service/lemon-http.service'
import { TositeKuvaService } from '../../_angular/service/tosite/tosite-kuva.service'

import { FirestoreTosite, FirestoreTositteenKuva, SAHKOISET_LASKUT_MAKSUTAPA_ID } from '@lemon/core-firebase/model/tosite'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { DateService } from '@lemon/core/service/date.service'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { AsiakasService, AsiakkaanAvainTiedot } from '../../_angular/service/asiakas/asiakas.service'

export interface ListausFirestoreKuitti extends FirestoreTosite {
  ensimmainenKuva: SafeUrl
  ladataan: boolean
  vihreaPilvi: boolean
  naytaLisaa: boolean
  kuvienMaara: number
  kuvaEpaonnistui: boolean
}

export interface VuosiKk {
  vuosi: number
  kk: number
}

export interface Hakukriteerit {
  naytaPoistetut: boolean
  vuosikk: VuosiKk
}

@Injectable()
export class TositeSelailuListausFirebaseDataSource extends DataSource<ListausFirestoreKuitti> {
  private lataaSubject = new BehaviorSubject<boolean>(true)
  public lataaObservable: Observable<boolean> = this.lataaSubject.asObservable()

  private _valittuMaksutapaSubject = new BehaviorSubject<number | null>(null)
  private _edellinenAsiakasAvain: string | null = null
  public valittuMaksutapaObservable: Observable<number | null> = this._valittuMaksutapaSubject.asObservable()

  private oletuskriteerit: Hakukriteerit = {
    vuosikk: {
      kk: new Date().getMonth(),
      vuosi: new Date().getFullYear(),
    },
    naytaPoistetut: false,
  }
  private hakukriteeritSubject = new BehaviorSubject<Hakukriteerit>(this.oletuskriteerit)
  public hakukriteeritObservable: Observable<Hakukriteerit> = this.hakukriteeritSubject.asObservable()

  private pageEventSubject = new BehaviorSubject<PageEvent>({
    length: 0,
    pageIndex: 0,
    pageSize: 20,
  })

  private sortSubject = new BehaviorSubject<Sort>({
    active: 'pvm',
    direction: 'desc',
  })

  private internalKuititObservable: Observable<ListausFirestoreKuitti[]>
  public kuukaudenKuititObservable: Observable<ListausFirestoreKuitti[]>
  public suodatetutKuititObservable: Observable<ListausFirestoreKuitti[]>
  public kuititObservable: Observable<ListausFirestoreKuitti[]>
  public rivienMaaraObservable: Observable<number>

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _tositeUriService: TositeUriService,
    private _kuittiKuvaService: TositeKuvaService,
    private _asiakasService: AsiakasService,
    private _kirjanpitoService: KirjanpitoService,
    private _dateService: DateService,
    private _timestampService: TimestampService,
  ) {
    super()

    this.internalKuititObservable = combineLatest([this.hakukriteeritObservable, this._asiakasService.nykyinenAsiakasAvainObservable]).pipe(
      tap(([, asiakas]) => {
        this.lataaSubject.next(true)
        const asiakasAvain = asiakas?.avain ?? null
        if (asiakasAvain !== this._edellinenAsiakasAvain) {
          this._edellinenAsiakasAvain = asiakasAvain
          this.vaihdaValittuMaksutapa(null)
        }
      }),
      switchMap(([hakukriteerit, asiakas]) => {
        if (!asiakas) {
          return of<ListausFirestoreKuitti[]>([])
        }

        const kuukaudenAlku = new Date(hakukriteerit.vuosikk.vuosi, hakukriteerit.vuosikk.kk, 1)
        const kuukaudenAlkuTs = this._timestampService.dateToTimestamp(this._dateService.kuukaudenEnsimmainen(kuukaudenAlku))
        const kuukaudenLoppuTs = this._timestampService.dateToTimestamp(this._dateService.kuukaudenViimeinen(kuukaudenAlku))

        const polku = this._tositeUriService.annaTositteidenFirestoreCollectionUri(asiakas.avain)
        const q = this._firebaseLemonaid.firestoreCollection<ListausFirestoreKuitti>(polku).whereFree('pvm', '>=', kuukaudenAlkuTs).whereFree('pvm', '<=', kuukaudenLoppuTs)

        return combineLatest([q.listen(), this._kirjanpitoService.kirjanpidonProjektitObservable, this.sortSubject]).pipe(
          map(([snapshots, projektit, sort]) => {
            const suodatetut = snapshots.filter(tosite => this._onkoListattava(tosite, hakukriteerit.naytaPoistetut))
            const projektiNimet = new Map((projektit ?? []).map(projekti => [projekti.avain, projekti.nimi]))
            return this.sorttaa(suodatetut, sort, projektiNimet)
          }),
          map(kuitit => {
            for (const kuitti of kuitit) {
              kuitti.kuvienMaara = Object.values(kuitti.kuvat ?? {}).filter(kuva => this._onNakyvaKuva(kuitti, kuva)).length
              kuitti.kuvaEpaonnistui = false
              // Hoida kuva käyttöliittymään
              const lopullinenKuva = this._kuittiKuvaService.annaKuvaLopullisestaKakusta(kuitti)
              if (lopullinenKuva) {
                kuitti.ladataan = false
                kuitti.ensimmainenKuva = lopullinenKuva
              } else {
                kuitti.ladataan = true
                this.paivitaKuva(kuitti, asiakas)
              }

              // Onko kuitti jo valmis?
              kuitti.vihreaPilvi = true
              if (kuitti.alkuperaiset) {
                for (const alkuperaisenAvain of Object.keys(kuitti.alkuperaiset)) {
                  const alkuperainen = kuitti.alkuperaiset[alkuperaisenAvain]
                  if (!alkuperainen.kasitelty) {
                    kuitti.vihreaPilvi = false
                    break
                  }
                }
              }
            }

            return kuitit
          }),
          tap(() => {
            this.lataaSubject.next(false)
          }),
        )
      }),
      lemonShare(),
    )

    this.kuukaudenKuititObservable = combineLatest([this.internalKuititObservable, this.lataaObservable]).pipe(
      map(([kuitit, lataa]) => {
        if (lataa) {
          return []
        }
        return kuitit
      }),
    )

    this.suodatetutKuititObservable = combineLatest([this.kuukaudenKuititObservable, this.valittuMaksutapaObservable]).pipe(
      map(([kuitit, maksutapa]) => (maksutapa === null ? kuitit : kuitit.filter(kuitti => kuitti.maksutapa === maksutapa))),
      lemonShare(),
    )

    this.rivienMaaraObservable = this.suodatetutKuititObservable.pipe(map(kuitit => kuitit.length))

    this.kuititObservable = combineLatest([this.suodatetutKuititObservable, this.pageEventSubject]).pipe(
      map(([kuitit, page]) => {
        const alku = page.pageIndex * page.pageSize
        return kuitit.slice(alku, alku + page.pageSize)
      }),
    )
  }

  private sorttaa(tositteet: ListausFirestoreKuitti[], sort: Sort, projektiNimet: Map<string, string>): ListausFirestoreKuitti[] {
    const kerroin = sort.direction === 'asc' ? 1 : -1

    return [...tositteet].sort((a, b) => {
      let tulos = 0
      if (sort.active === 'toimittaja') {
        tulos = this.vertaaToimittajat(a, b) * kerroin
      } else if (sort.active === 'summa') {
        tulos = this.vertaaSummat(a, b) * kerroin
      } else if (sort.active === 'maksutapa') {
        tulos = this.vertaaMaksutavat(a, b) * kerroin
      } else if (sort.active === 'projekti') {
        tulos = this.vertaaProjektit(a, b, projektiNimet) * kerroin
      } else {
        tulos = this.vertaaPvmt(a, b) * kerroin
      }
      if (tulos !== 0) {
        return tulos
      }

      tulos = this.vertaaPvmt(b, a)
      if (tulos !== 0) {
        return tulos
      }
      tulos = this.vertaaToimittajat(a, b)
      if (tulos !== 0) {
        return tulos
      }
      return this.vertaaSummat(a, b)
    })
  }

  private _onkoListattava(tosite: FirestoreTosite, naytaPoistetut: boolean): boolean {
    if (tosite.x || tosite.type === 'payment') {
      return false
    }
    if (tosite.poistettu === true && !naytaPoistetut) {
      return false
    }
    if (tosite.type === 'einvoice' && tosite.maksutapa !== SAHKOISET_LASKUT_MAKSUTAPA_ID) {
      return false
    }
    if (tosite.einvoiceApprovalStatus === 'rejected' || tosite.einvoiceApprovalStatus === 'pending-approval') {
      return false
    }
    // Uuden vastaanoton kautta tullut lasku näytetään vasta, kun sille on merkitty maksupäivä
    if (tosite.paymentStatus && !tosite.paymentDate) {
      return false
    }
    return true
  }

  get page(): PageEvent {
    return this.pageEventSubject.value
  }

  getValittuMaksutapa(): number | null {
    return this._valittuMaksutapaSubject.value
  }

  vaihdaValittuMaksutapa(maksutapa: number | null): void {
    if (this._valittuMaksutapaSubject.value === maksutapa) {
      return
    }
    this._valittuMaksutapaSubject.next(maksutapa)
    this._resetoiSivutus()
  }

  getSort(): Sort {
    return this.sortSubject.value
  }

  changePage(pageEvent: PageEvent): void {
    this.pageEventSubject.next(pageEvent)
  }

  changeSort(sort: Sort): void {
    this.sortSubject.next(sort)
    this._resetoiSivutus()
  }

  getVuosiKk(): VuosiKk {
    return this.hakukriteeritSubject.value.vuosikk
  }

  getNaytaPoistetut(): boolean {
    return this.hakukriteeritSubject.value.naytaPoistetut
  }

  changeVuosiKk(vuosi: number, kk: number): void {
    const nykyisetArvot = this.hakukriteeritSubject.value
    if (nykyisetArvot.vuosikk.vuosi === vuosi && nykyisetArvot.vuosikk.kk === kk) {
      return
    }
    this.hakukriteeritSubject.next({ ...nykyisetArvot, vuosikk: { vuosi, kk } })
    this._resetoiSivutus()
  }

  changeNaytaPoistetut(naytaPoistetut: boolean): void {
    const nykyisetArvot = this.hakukriteeritSubject.value
    this.hakukriteeritSubject.next({ ...nykyisetArvot, naytaPoistetut })
    this._resetoiSivutus()
  }

  private _resetoiSivutus(): void {
    const nykyinenPage = this.pageEventSubject.value
    if (nykyinenPage.pageIndex === 0) {
      return
    }
    this.pageEventSubject.next({ ...nykyinenPage, pageIndex: 0 })
  }

  connect(): Observable<ListausFirestoreKuitti[]> {
    return this.kuititObservable
  }

  disconnect() {}

  private _onNakyvaKuva(kuitti: ListausFirestoreKuitti, kuva: FirestoreTositteenKuva) {
    if (kuva.poistettu) {
      return false
    }
    if (!kuva.alkuperaisenAvain) {
      return true
    }
    const alkuperainen = kuitti.alkuperaiset?.[kuva.alkuperaisenAvain]
    return !!alkuperainen && !alkuperainen.poistettu
  }

  private paivitaKuva(kuitti: ListausFirestoreKuitti, asiakas: AsiakkaanAvainTiedot) {
    let kuva: FirestoreTositteenKuva = null
    // eslint-disable-next-line @typescript-eslint/no-shadow
    for (const kuvanAvain of Object.keys(kuitti?.kuvat || {})) {
      const mahdollinen = kuitti.kuvat[kuvanAvain]
      if (this._onNakyvaKuva(kuitti, mahdollinen) && (kuva === null || mahdollinen.jarjestys < kuva.jarjestys)) {
        kuva = mahdollinen
        if (kuva.jarjestys === 1) {
          break
        }
      }
    }

    if (!kuva) {
      kuitti.ladataan = false
      kuitti.ensimmainenKuva = '/assets/noimage-error.jpg'
      return
    }

    const kuvanAvain = kuva.avain + '_thumb'
    const kuvanUrl = this._tositeUriService.annaCloudStorageKuvaUri(asiakas.asiakasId + '', kuitti.kuvakansio, kuvanAvain, 'webp')
    kuitti.ensimmainenKuva = LEMONATOR_HTTPS_IMAGES_API + '/api/1/kuvat/' + kuvanUrl
  }

  private vertaaPvmt(a: FirestoreTosite, b: FirestoreTosite): number {
    const pvmA = this._dateService.localDateToNumber(a.localPvm) ?? 0
    const pvmB = this._dateService.localDateToNumber(b.localPvm) ?? 0
    return pvmA - pvmB
  }

  private vertaaToimittajat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.seller ?? a.el ?? '').localeCompare(b.seller ?? b.el ?? '', 'fi')
  }

  private vertaaSummat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.sum ?? a.summa ?? 0) - (b.sum ?? b.summa ?? 0)
  }

  private vertaaMaksutavat(a: FirestoreTosite, b: FirestoreTosite): number {
    return (a.maksutapa ?? 0) - (b.maksutapa ?? 0)
  }

  private vertaaProjektit(a: FirestoreTosite, b: FirestoreTosite, projektiNimet: Map<string, string>): number {
    const nimiA = projektiNimet.get(a.projektiAvain ?? '') ?? ''
    const nimiB = projektiNimet.get(b.projektiAvain ?? '') ?? ''
    return nimiA.localeCompare(nimiB, 'fi')
  }
}
